/**
 * @file Planboard grid bar component.
 * @author Heino Lenting
 */
import * as tslib_1 from "tslib";
import { AfterViewInit, AfterContentChecked, ElementRef, NgZone, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { GridDisplayBaseComponent } from '../grid-display-base/grid-display-base.component';
import { DataSet } from '../../core/helpers/data-set';
import { DomainService, PROP_ORIGINAL_RESOURCE_OID } from '../../domain.service';
import { KeyedCollection } from '../../base/helpers/remove-keyed-collection';
import { LocalisationService } from '../../core/localisation.service';
import { SettingsService } from '../../settings.service';
import { Router } from '@angular/router';
import { OverlayService } from '../../core/overlay.service';
import { ExecutorsService } from '../executors.service';
import { AlertsService } from '../../core/alerts.service';
import { DropTargetsService } from '../drop-targets.service';
import { EventsService } from '../../core/event.service';
import { GridService } from '../grid.service';
import { RoutesService } from '../../routes.service';
import { BackendSettingsService } from '../../backend-settings.service';
var PlanboardGridDisplayComponent = /** @class */ (function (_super) {
    tslib_1.__extends(PlanboardGridDisplayComponent, _super);
    /**
     * Creates an instance of PlanboardGridDisplayComponent.
     * @param {DomainService} domainService
     * @param {LocalisationService} localisationService
     * @param {configurationsService} configurationsService
     * @param {SettingsService} settingsService
     */
    function PlanboardGridDisplayComponent(router, domainService, localisationService, settingsService, backendSettingsService, overlayService, executorsService, dropZonesService, alertsService, elementRef, ngZone, changeDetector, eventsService, gridService) {
        var _this = _super.call(this, router, domainService, localisationService, settingsService, backendSettingsService, overlayService, executorsService, alertsService, elementRef, ngZone, changeDetector, eventsService, gridService) || this;
        _this.dropZonesService = dropZonesService;
        /** The dataset per resource witht the OID as key  */
        _this._datasets = new KeyedCollection();
        _this.subscriptionPool.add(eventsService.register('drop-targets-changed').subscribe(function () {
            changeDetector.detectChanges();
        }));
        _this.subscriptionPool.add(eventsService.register('expand-grid-changed').subscribe(function () {
            changeDetector.detectChanges();
        }));
        _this.subscriptionPool.add(eventsService.register('plugin-selector-changed').subscribe(function () {
            changeDetector.detectChanges();
        }));
        return _this;
    }
    Object.defineProperty(PlanboardGridDisplayComponent.prototype, "resources", {
        /**
         * Gets the resources
         * @returns {DataSet} The resources
         */
        get: function () {
            return this._resources;
        },
        /**
         * Sets the resources
         * @param {DataSet} The resources
         */
        set: function (newValue) {
            this._resources = newValue;
            this.generateGridSegments();
            this.refresh();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(PlanboardGridDisplayComponent.prototype, "startDate", {
        /** The start date used for building the query */
        get: function () {
            if (this.gridService.level.formatter.queryStartDate == null) {
                return '2017-01-01';
            }
            else {
                return this.gridService.level.formatter.queryStartDate.format('YYYY-MM-DD HH:mm:ss');
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(PlanboardGridDisplayComponent.prototype, "endDate", {
        /** The end date used for building the query */
        get: function () {
            if (this.gridService.level.formatter.queryFinishDate == null) {
                return '2017-02-01';
            }
            else {
                return this.gridService.level.formatter.queryFinishDate.format('YYYY-MM-DD HH:mm:ss');
            }
        },
        enumerable: true,
        configurable: true
    });
    /** Lifecycle hook that is called after every check of a directive's content. */
    PlanboardGridDisplayComponent.prototype.ngAfterContentChecked = function () {
        if (this._gridCollections === undefined) {
            this._gridCollections = this.generateGridSegments();
        }
    };
    PlanboardGridDisplayComponent.prototype.ngOnDestroy = function () {
        _super.prototype.ngOnDestroy.call(this);
    };
    /**
     * Retrieves a new fresh data set form the backend when query parameters are changed.
     * @param {Boolean} [forcing=false] Refresh is forced when true otherwise only when query parameters are changed.
     * @returns {Observable<QueryResult>} An observable of the needed retrieve request otherwise null.
     */
    PlanboardGridDisplayComponent.prototype.refresh = function (forcing) {
        if (forcing === void 0) { forcing = false; }
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var resources, count;
            var _this = this;
            return tslib_1.__generator(this, function (_a) {
                resources = this.resources.items.map(function (entity) { return entity; });
                count = 0;
                resources.forEach(function (resource) { return tslib_1.__awaiter(_this, void 0, void 0, function () {
                    var oid, dataset, queryOptions, _a, formatter;
                    return tslib_1.__generator(this, function (_b) {
                        switch (_b.label) {
                            case 0:
                                oid = resource['Oid'];
                                dataset = this._datasets.Item(oid);
                                if (dataset === undefined) {
                                    dataset = this._datasets.Add(oid, new DataSet());
                                    dataset.sourceName = 'TaskList';
                                }
                                this.isLoading = true;
                                queryOptions = this.queryOptions(oid);
                                dataset.queryOptions = queryOptions;
                                _a = this;
                                return [4 /*yield*/, this.domainService.getDataSet(dataset, dataset.items)];
                            case 1:
                                _a.loadingInProgress = _b.sent();
                                formatter = this.gridService.level.formatter;
                                formatter.refreshWith(oid, dataset.items);
                                resource.$setRow(dataset);
                                this.detectChanges();
                                count++;
                                if (this.resources.items.length === count) {
                                    this.loadingInProgress = null;
                                    this.isLoading = false;
                                    this.detectChanges();
                                }
                                return [2 /*return*/];
                        }
                    });
                }); });
                // Stop loading even when there are no resources
                if (this.resources.items.length === 0) {
                    this.loadingInProgress = null;
                    this.isLoading = false;
                    this.detectChanges();
                }
                this.eventsService.register('grid-refresh').emit();
                return [2 /*return*/];
            });
        });
    };
    /**
     * Handling the move resize event.
     * An override of the method to accommodate refreshing with detectChanges.
     * @param {MouseEvent} event The mouse move event.
     */
    PlanboardGridDisplayComponent.prototype.moveResize = function (event) {
        // Planbord – Sommeer uren leeg #2524
        if (!this.itemResizeData) {
            return;
        }
        _super.prototype.moveResize.call(this, event);
        this.detectChanges();
    };
    /**
     * Lifecycle hook that is called after a component's view has been fully initialized.
     */
    PlanboardGridDisplayComponent.prototype.ngAfterViewInit = function () {
        var _this = this;
        this.changeDetector.detach();
        _super.prototype.ngAfterViewInit.call(this);
        this.subscriptionPool.add(this.resources.onRefresh.subscribe(function () {
            _this._gridCollections = _this.generateGridSegments();
            _this.refresh();
            // Register both grid collections and resources so that the drop zone services
            // can influence the planboard (no drop zones etc) during dragging
            _this.dropZonesService.registerGridCollections(_this._gridCollections);
            _this.dropZonesService.registerResources(_this.resources);
            // this._segments = this.generateSegments();
        }));
    };
    /**
     * Refresh the current dataset.
     */
    PlanboardGridDisplayComponent.prototype.userRefresh = function () {
        this.refresh();
    };
    /**
     * Updates the planboard after an entity is changed and saved to update the planboard.
     * @param {Entity} entity The changed entity.
     * @param {boolean} selectable Flag indicating whether or not to select the entity when needed.
     */
    PlanboardGridDisplayComponent.prototype.refreshFor = function (entity, selectable) {
        if (selectable === void 0) { selectable = true; }
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var oid, dataset, resource, result, itemOid_1, planItemNew, wrapper, oidOriginal, datasetOriginal, resourceOriginal;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        oid = entity['Origin'] ? entity['Origin'] : entity['Resource_Oid'] ? entity['Resource_Oid'] : entity[PROP_ORIGINAL_RESOURCE_OID];
                        if (oid == null) {
                            return [2 /*return*/];
                        }
                        dataset = this._datasets.Item(oid);
                        resource = this._resources.items.find(function (r) { return r['Oid'] === oid; });
                        if (!dataset) return [3 /*break*/, 2];
                        return [4 /*yield*/, this.domainService.getDataSet(dataset, dataset.items)];
                    case 1:
                        result = _a.sent();
                        this.gridService.level.formatter.refreshWith(oid, dataset.items);
                        if (RoutesService.getDetailId(this.router.routerState) != null) {
                            itemOid_1 = entity['Oid'] ? entity['Oid'] : entity['Item_Oid'];
                            if (this.oidOfDetailUrl(this.router.url) === itemOid_1) {
                                planItemNew = dataset.items.find(function (item) { return item['Item_Oid'] === itemOid_1; });
                                if (planItemNew) {
                                    if (selectable) {
                                        wrapper = this.findWrapperOf(planItemNew['Item']);
                                        _super.prototype.selectPlanItem.call(this, wrapper);
                                    }
                                }
                                else {
                                    // Kon de item niet vinden ik moet details sluiten.
                                    _super.prototype.selectPlanItem.call(this, null);
                                }
                            }
                        }
                        if (resource) {
                            resource.$setRow(dataset);
                        }
                        this.detectChanges();
                        return [3 /*break*/, 3];
                    case 2:
                        if (RoutesService.getDetailId(this.router.routerState) != null) {
                            _super.prototype.selectPlanItem.call(this, null);
                            this.detectChanges();
                        }
                        _a.label = 3;
                    case 3:
                        oidOriginal = entity[PROP_ORIGINAL_RESOURCE_OID];
                        if (oidOriginal == null) {
                            return [2 /*return*/];
                        }
                        // Opruimen tijdelijke properties
                        delete entity[PROP_ORIGINAL_RESOURCE_OID];
                        datasetOriginal = this._datasets.Item(oidOriginal);
                        resourceOriginal = this._resources.items.find(function (r) { return r['Oid'] === oidOriginal; });
                        if (!datasetOriginal) return [3 /*break*/, 5];
                        // Original Resource wordt getoond.
                        return [4 /*yield*/, this.domainService.getDataSet(datasetOriginal, datasetOriginal.items)];
                    case 4:
                        // Original Resource wordt getoond.
                        _a.sent();
                        // Refresh display.
                        this.gridService.level.formatter.refreshWith(oidOriginal, datasetOriginal.items);
                        if (resourceOriginal) {
                            resourceOriginal.$setRow(datasetOriginal);
                            this.detectChanges();
                        }
                        _a.label = 5;
                    case 5: return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Create the QueryOptions for given resource
     *
     * @param {string} resourceOid
     * @returns {QueryOptions}
     */
    PlanboardGridDisplayComponent.prototype.queryOptions = function (resourceOid) {
        return {
            parameters: {
                'className': 'Resource',
                'oid': resourceOid,
                'start': this.startDate,
                'finish': this.endDate,
                'include': 'Assignments Appointments Availabilities Attendants WorkPatterns Violations'
            },
            expand: 'Item.Deployment.Demand.Task.Project, Item.Service, Item.Service.Shift'
        };
    };
    /**
     *  Generate the grid segements based on current state of the horizon
     *
     * @returns {GridCollections}
     */
    PlanboardGridDisplayComponent.prototype.generateGridSegments = function () {
        return this.gridService.level.formatter.planboardGridSegments(this, this.gridService);
    };
    // Used for check if the current segment needs expansion
    PlanboardGridDisplayComponent.prototype.expandGrid = function (segmentsIndex, segmentIndex) {
        if (this.expandedIndex === undefined) {
            return false;
        }
        return (segmentsIndex === this.expandedIndex);
    };
    PlanboardGridDisplayComponent.prototype.selectPlanItem = function (item) {
        _super.prototype.selectPlanItem.call(this, item);
        this.detectChanges();
    };
    /**
     * Override and use this method to load items for first time after creation of component.
     * The method should wait for all the data needed to load items before loading.
     * @protected
     * @returns {boolean} True when items are loaded otherwise false.
     */
    PlanboardGridDisplayComponent.prototype.initialLoading = function () {
        var _this = this;
        this.subscriptionPool.add(this.gridService.zoomChanged.subscribe(function () {
            _this.detectChanges();
        }));
        return _super.prototype.initialLoading.call(this);
    };
    return PlanboardGridDisplayComponent;
}(GridDisplayBaseComponent));
export { PlanboardGridDisplayComponent };
